import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useOrganizationGroupsQuery,
  useOrganizationVehiclesQuery,
} from "../../api/network/adminApiServices";
import {
  setModalsData,
  TModalsState,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { OrganizationEntityListingPayload } from "../../api/types/Admin";
import {
  SPEED_UNITS_OPTIONS,
  SPEED_UNIT_VALUES,
} from "../admin-details/account/constants";
import { LegacyGroupEntityListingPayload } from "../../api/types/Group";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import BasicMap, { MapLoadingAnimation } from "../../components/maps/basicMap";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { TMapState, TMapType } from "../../types/map";
import {
  convertKmphTo,
  speedUnitSuffix,
} from "../../utils/distance";
import { useLoggedInUserData } from "../../utils/user";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { IGNITION_STATUS_CODES } from "../map-asset/constants";
import { todayDateKey } from "../map-asset/multiDay/dateUtils";
import {
  RouteRenderMode,
  TimedPathPoint,
} from "../map-asset/multiDay/resolveRoutePath";
import { isNonTrip } from "../map-asset/multiDay/tripClassification";
import ModalEventFilter from "../map-asset/modalEventFilter";
import { StatusCodeFilter } from "../map-asset/statusCodeFilter";
import ModalVehicleTripEventDetails from "../map-asset/modalVehicleTripEventDetails";
import { TMapRef } from "../map-asset/type";
import { calculatedTimeFilters } from "../map-asset/util/timeFilter";
import RefreshCountdownBar from "../../components/maps/refreshCountdownBar";
import { ReplayControlsPanel } from "../map-asset/multiDay/ReplayControlsPanel";
import { tripKeyFor } from "../map-asset/multiDay/renderMultiVehicleRoutes";
import { useMultiVehicleMapRender } from "../map-asset/multiDay/useMultiVehicleMapRender";
import { CompareVehicleRoutesModal } from "./CompareVehicleRoutesModal";
import { DayStepper } from "./DayStepper";
import {
  SelectableVehicle,
  useMultiVehicleTripData,
} from "./useMultiVehicleTripData";
import { useResolvedRoutePaths } from "./useResolvedRoutePaths";
import { useRouteOverlayPaths } from "../map-asset/multiDay/useRouteOverlayPaths";
import {
  overlayRouteModes,
  primaryRouteMode,
  routeViewIsLocked,
  RouteViewsEnabled,
  toggleRouteView,
} from "../map-asset/multiDay/routeViewModes";
import { useVisibleTripKeys } from "./useVisibleTripKeys";
import { useVehicleEventReplay } from "./useVehicleEventReplay";
import { VehicleCompareView } from "./VehicleCompareView";
import { VehicleSelector } from "./VehicleSelector";
import { VehicleTripDetailPanel } from "./VehicleTripDetailPanel";
import { VehicleTripList } from "./VehicleTripList";

// side-by-side maps in the compare view - one per vehicle
const MAX_COMPARE_VEHICLES = 4;

// stable identity - a `[]` literal at the call site would be a fresh array
// every render, restarting the trip fetch on each one
const NO_VEHICLES: SelectableVehicle[] = [];

// these stack, the same as on the asset map: one of them draws the line
// that carries the pins and the replay, and the rest are drawn over it as
// comparison lines
const ROUTE_VIEW_OPTIONS: { mode: RouteRenderMode; label: string; icon: string }[] =
  [
    { mode: "DEFAULT", label: "Default", icon: svgIcons.LineStyleIconDefault },
    {
      mode: "POINT_TO_POINT",
      label: "Point to Point",
      icon: svgIcons.LineStyleIconP2P,
    },
    {
      mode: "SNAP_TO_ROAD",
      label: "Snap to Road",
      icon: svgIcons.LineStyleIconSnap,
    },
    {
      mode: "DIRECTIONS_SERVICE",
      label: "Direction Service",
      icon: svgIcons.LineStyleIconDirSvc,
    },
  ];


const ScreenMapMultiVehicle = () => {
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  // account settings that drive how a vehicle is labelled and how speed is
  // shown - same lookups the rest of the app already uses (e.g. the shared
  // event-detail modal), so this map reads them the same way everyone else does
  const showDriver = useLoggedInUserData("hasdrivers") === "1";
  const orgSpeedUnitValue = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnitValue)
      ?.unit ?? SPEED_UNITS_OPTIONS[0].unit;
  const formatSpeed = (speedKph: number | null | undefined) =>
    speedKph == null
      ? null
      : `${Math.round(convertKmphTo(speedUnit, speedKph))} ${speedUnitSuffix(
          speedUnit
        )}`;
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const mapRef = useRef<TMapRef>({
    map: null,
    objects: {
      mTripLayers: [],
      mDirectionsManagers: [],
      mTripsVisibility: [],
      mWaypoints: [],
      mPushpins: [],
      mClusterLayer: null,
      mTripsData: [],
    },
    routeViewsEnabled: {
      DEFAULT: true,
      POINT_TO_POINT: false,
      SNAP_TO_ROAD: false,
      DIRECTIONS_SERVICE: false,
    },
  });

  // the map script isn't loaded globally - each map page kicks it off
  const { initMap } = useMapInit();
  const { gMapSetMode } = useGMapMode();
  useEffect(() => {
    if (!mapState?.mapScriptLoaded) initMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BasicMap creates the google.maps.Map instance and only then fills in
  // mapRef.current.map, so the renderer can't run until this fires
  const [isMapReady, setIsMapReady] = useState(false);
  const handleMapReady = () => {
    const map = mapRef.current?.map;
    if (map && mapState?.mapType) {
      gMapSetMode(map, mapState.mapType as TMapType, map.getCenter());
    }
    setIsMapReady(true);
  };

  const [selectedVehicles, setSelectedVehicles] = useState<SelectableVehicle[]>(
    []
  );
  // reopening the picker over an existing selection, rather than throwing it
  // away, so vehicles can be added or dropped without starting over
  const [isChangingSelection, setIsChangingSelection] = useState(false);
  const hasSelection = selectedVehicles.length > 0 && !isChangingSelection;
  // Route views stack here the same way they do on the asset map: the
  // alternates can be ticked together so the raw GPS line and the
  // road-matched line can be read against each other. This was a single
  // choice, which is why the two maps disagreed.
  const [routeViews, setRouteViews] = useState<RouteViewsEnabled>({
    DEFAULT: true,
    POINT_TO_POINT: false,
    SNAP_TO_ROAD: false,
    DIRECTIONS_SERVICE: false,
  });
  // whether the picker lists vehicles that had no trip that day. it decides
  // what is fetched: the default is only vehicles with a trip, filtered by
  // the server, so a large fleet's first page is not spent on vehicles that
  // are then hidden client-side
  const [showInactiveVehicles, setShowInactiveVehicles] = useState(false);
  const [routeViewsVersion, setRouteViewsVersion] = useState(0);
  // the view the real line draws; the rest are comparison lines
  const effectiveRouteMode = primaryRouteMode(routeViews);
  const overlayModes = useMemo(
    () => overlayRouteModes(routeViews),
    [routeViews]
  );

  // the renderer reads line style off mapRef, the same place the single-day
  // view keeps it, so the two stay in sync
  const applyRouteMode = (mode: RouteRenderMode) => {
    setRouteViews((current) => toggleRouteView(current, mode));
  };

  useEffect(() => {
    mapRef.current.routeViewsEnabled = routeViews;
    setRouteViewsVersion((version) => version + 1);
  }, [routeViews]);
  const [selectedTripPoint, setSelectedTripPoint] =
    useState<VehicleMapDataPoint | null>(null);
  // detail view holds a set: one trip from a list row, or every selected
  // trip for an asset when its route or header is clicked. activeTripKey is
  // the one expanded in the panel.
  const [detailedTripKeys, setDetailedTripKeys] = useState<string[]>([]);
  const [activeTripKey, setActiveTripKey] = useState<string | null>(null);
  const activeTripKeyRef = useRef<string | null>(null);
  activeTripKeyRef.current = activeTripKey;
  const [hoveredTrip, setHoveredTrip] = useState<{
    tripKey: string;
    source: "map" | "list";
  } | null>(null);

  // Picking an event - from a pin on the map or a row in the trip's detail
  // - opens the same popup the asset map uses. It is gated on redux modal
  // state, so selecting the point is not enough on its own.
  //
  // modalsState is deliberately not a dependency: it changes as a result of
  // this dispatch, and depending on it would loop.
  useEffect(() => {
    if (!selectedTripPoint) return;
    dispatch(
      setModalsData({
        ...modalsState,
        showLayerFilter: false,
        showEventFilter: false,
        showVehicleFilter: false,
        showVehicleDetails: true,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTripPoint, dispatch]);

  // The map's highlight follows whatever is selected, wherever it was
  // picked - so clicking a row in the event list marks that pin on the map,
  // the mirror of clicking a pin scrolling the list to its row. Nulls are
  // ignored: picking an event flushes this to null for 100ms before the new
  // point lands, and acting on that would blink the highlight off between
  // every pick. Clearing is done by the popup's close handler instead.
  useEffect(() => {
    if (selectedTripPoint) mapRender.highlightEventPoint(selectedTripPoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTripPoint]);

  // on by default - where routes pile up is worth seeing without having to
  // ask for it, and it can be switched off from the top bar
  const [overlapsEnabled, setOverlapsEnabled] = useState(true);

  // which event types get a pin - same Event Filter the asset map uses,
  // account-aware since the options list (and any custom naming) comes from
  // the vehicles actually on screen. null means the filter hasn't been
  // applied, i.e. everything; an empty array means nothing.
  const [filteredStatusCodes, setFilteredStatusCodes] =
    useState<StatusCodeFilter>(null);

  const [compareTripKeys, setCompareTripKeys] = useState<string[]>([]);
  const [isComparing, setIsComparing] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [showRouteViewsPopup, setShowRouteViewsPopup] = useState(false);
  // collapsed by default, same as the Fleet Map's live-updates card
  const [liveUpdatesExpanded, setLiveUpdatesExpanded] = useState(false);
  const timezone = accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE;
  // one day is plotted at a time; the day stepper in the panel header moves
  // which day that is. capped at today - there's nothing to plot ahead.
  const [dateKey, setDateKey] = useState(todayDateKey());
  const dayStartTimestamp = useMemo(
    () => calculatedTimeFilters(dateKey, timezone)?.calculatedUnixTimestampFrom ?? 0,
    [dateKey, timezone]
  );
  // the selector counts the events in the same window the map is plotted
  // from, so the number it shows is the one the plot request is judged on
  const dayEndTimestamp = useMemo(
    () => calculatedTimeFilters(dateKey, timezone)?.calculatedUnixTimestampTo ?? 0,
    [dateKey, timezone]
  );


  // the comparison grid covers the map, so anything that plays out on the
  // map has to leave it first
  const openTripDetail = (tripKeys: string[], focusTripKey?: string) => {
    if (tripKeys.length > 0) setIsComparing(false);
    setDetailedTripKeys(tripKeys);
    setActiveTripKey(
      focusTripKey ?? (tripKeys.length === 1 ? tripKeys[0] : null)
    );
  };

  // back at the overview, so introduce it again - see the showcase below
  const closeTripDetail = () => {
    openTripDetail([]);
    requestShowcase();
  };

  // picked out of an overlap badge - the asset's whole day opens with the
  // trip that was clicked singled out, so the rest are one click away
  const openAssetDetailIsolating = (tripKey: string) => {
    const deviceId = tripKey.split("__")[0];
    const group = vehicleGroups.find((item) => item.deviceId === deviceId);
    const keys =
      group?.trips
        .map((_, tripIndex) => tripKeyFor(deviceId, tripIndex))
        .filter((key) => selectedTripKeys.has(key)) ?? [];
    openTripDetail(keys.length > 0 ? keys : [tripKey], tripKey);
  };

  // the vehicle header's "Details" - the whole asset's day, nothing singled
  // out, so every trip draws and every trip's events drop
  const openAssetDetail = (deviceId: string) => {
    const group = vehicleGroups.find((item) => item.deviceId === deviceId);
    const keys =
      group?.trips
        .map((_, tripIndex) => tripKeyFor(deviceId, tripIndex))
        .filter((key) => selectedTripKeys.has(key)) ?? [];
    if (keys.length > 0) openTripDetail(keys);
  };

  const orgVehiclesQueryParams: OrganizationEntityListingPayload = {
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    // one page has to hold the whole list: the selector filters, groups and
    // counts client-side, so whatever the page leaves out is silently
    // dropped. Both lists ask for the endpoint's maximum - a large account
    // can put well over a thousand vehicles on the road in a day, so capping
    // the trips-only list lower just moves the cut-off rather than removing
    // it.
    page_size: VEHICLE_LIST_PAGE_SIZE,
    search: "",
    is_active: "active",
    trip_date_start: dayStartTimestamp,
    trip_date_end: dayStartTimestamp + 24 * 60 * 60 - 1,
    has_trips: !showInactiveVehicles,
  };

  const { data: dataOrgVehicles, isFetching: isFetchingOrgVehicles } =
    useOrganizationVehiclesQuery(orgVehiclesQueryParams, {
      skip: !mapState?.mapScriptLoaded,
      refetchOnMountOrArgChange: true,
    });

  // groups only drive the selector's "By Group" tab - membership itself
  // rides along on each vehicle, so no per-group lookup is needed
  const orgGroupsQueryParams = useRef<LegacyGroupEntityListingPayload>({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    search: "",
  });
  const { data: dataOrgGroups } = useOrganizationGroupsQuery(
    orgGroupsQueryParams.current,
    {
      skip: !mapState?.mapScriptLoaded,
      refetchOnMountOrArgChange: true,
    }
  );


  // while the picker is reopened over an existing selection, selectedVehicles
  // still holds the *previous* confirmed set - changing the date in there
  // (to refresh its "has a trip today" filter) must not refetch that stale
  // set in the background. Nothing to show until Confirm updates both
  // selectedVehicles and isChangingSelection together.
  const { vehicleGroups, isFetching, refetch } = useMultiVehicleTripData({
    accountId: `${thisUserOrganizationId}`,
    vehicles: isChangingSelection ? NO_VEHICLES : selectedVehicles,
    dateKey,
    timezone,
  });

  const [selectedTripKeys, setSelectedTripKeys] = useState<Set<string>>(
    new Set()
  );

  // Arriving at the overview draws every trip in and leaves them there. The
  // fetch is expensive, so showing nothing for it wastes the wait - and the
  // routes staying put is the whole point of the animation. Getting down to
  // one or two vehicles is what Clear and the filters are for.
  const [showcaseNonce, setShowcaseNonce] = useState(0);
  const requestShowcase = () => setShowcaseNonce((nonce) => nonce + 1);

  // Up to this many trips, every route is put on the road straight away -
  // the same thing the single-day asset map does, and the cost is small
  // enough not to think about. Past it a whole fleet's worth of paid Google
  // lookups isn't worth paying for a shift you can't see at that zoom, so
  // alignment follows what's being looked at instead.
  //
  // Counted in trips rather than vehicles: the cost is one lookup per trip,
  // and a single vehicle can easily run sixty trips in a day.
  const EAGER_ALIGNMENT_TRIP_LIMIT = 12;
  const alignEverything = selectedTripKeys.size <= EAGER_ALIGNMENT_TRIP_LIMIT;

  // trips near enough to be looked at closely - see useVisibleTripKeys
  const visibleTripKeys = useVisibleTripKeys({
    mapRef,
    vehicleGroups,
    selectedTripKeys,
    isMapReady,
    enabled: !alignEverything,
  });

  const tripsToResolve = useMemo(() => {
    const wanted = alignEverything
      ? null
      : new Set<string>([
        ...detailedTripKeys,
        ...(isComparing ? compareTripKeys : []),
        ...visibleTripKeys,
      ]);
    const list: {
      tripKey: string;
      tripIndex: number;
      points: VehicleMapDataPoint[];
    }[] = [];
    vehicleGroups.forEach((group) => {
      group.trips.forEach((points, tripIndex) => {
        const tripKey = tripKeyFor(group.deviceId, tripIndex);
        if (!selectedTripKeys.has(tripKey)) return;
        if (wanted && !wanted.has(tripKey)) return;
        list.push({ tripKey, tripIndex, points });
      });
    });
    return list;
  }, [
    vehicleGroups,
    selectedTripKeys,
    alignEverything,
    detailedTripKeys,
    compareTripKeys,
    isComparing,
    visibleTripKeys,
  ]);

  // held here rather than inside either hook: the renderer reads it while
  // drawing, and the resolver writes into it as each trip comes back
  const resolvedPathsRef = useRef<Map<string, TimedPathPoint[]>>(new Map());

  // declared before the renderer: while an event replay owns the map the
  // routes are torn down, so the replay is not drawing over them
  const eventReplay = useVehicleEventReplay({
    mapRef,
    vehicleGroups,
    speedUnit,
    filteredStatusCodes,
  });

  const mapRender = useMultiVehicleMapRender({
    mapRef,
    vehicleGroups,
    selectedTripKeys,
    resolvedPathsRef,
    isDataReady: isMapReady && !isFetching,
    routeViewsVersion,
    hoveredTripKey: hoveredTrip?.tripKey ?? null,
    detailedTripKeys,
    activeDetailTripKey: activeTripKey,
    overlapsEnabled,
    filteredStatusCodes,
    paused: eventReplay.isReplaying,
    speedUnit,
    timezone,
    onMapHoverTrip: (tripKey) =>
      setHoveredTrip(tripKey ? { tripKey, source: "map" } : null),
    onClusterTripSelect: openAssetDetailIsolating,
    onTripPointClick: (tripKey, point, source) => {
      // An event pin only exists on a trip whose detail is already open, so
      // clicking one is never a request to change what is on the map.
      // Re-opening the set here would take every pin down and redraw the
      // route out from under the event that was just clicked - which is
      // what used to make the events vanish on the first click.
      if (source !== "pin") {
        const routeIsInFocus = activeTripKeyRef.current === tripKey;
        // clicking a route opens that trip on its own, the same as the asset
        // map. the whole day is the vehicle header's "Details" instead.
        openTripDetail([tripKey]);
        if (!routeIsInFocus) return;
      }
      setSelectedTripPoint(null);
      setTimeout(() => setSelectedTripPoint(point), 100);
    },
  });

  // routes go up on their raw fixes immediately and are swapped for their
  // road-aligned versions one by one as those resolve
  useResolvedRoutePaths({
    trips: tripsToResolve,
    mode: effectiveRouteMode,
    pathsRef: resolvedPathsRef,
    onResolved: mapRender.applyResolvedPath,
    onAllResolved: mapRender.refreshOverlapClusters,
  });

  // and the same again for whatever extra views are ticked, which draw as
  // comparison lines rather than replacing the one above
  useRouteOverlayPaths({
    trips: tripsToResolve,
    modes: overlayModes,
    onResolved: mapRender.applyOverlayPath,
  });

  // A fresh set of trips - a new day, or a different pick of vehicles.
  // Deliberately not keyed on isFetching alone: the auto-refresh refetches
  // the same day's data every 30s and must not replay the whole fleet.
  const dataSignature = `${dateKey}|${selectedVehicles
    .map((vehicle) => vehicle.deviceId)
    .sort()
    .join(",")}`;
  const showcasedSignatureRef = useRef("");
  useEffect(() => {
    if (isFetching || !hasSelection || vehicleGroups.length === 0) return;
    if (showcasedSignatureRef.current === dataSignature) return;
    showcasedSignatureRef.current = dataSignature;
    requestShowcase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, hasSelection, vehicleGroups, dataSignature]);

  // select everything the showcase is going to draw
  useEffect(() => {
    if (showcaseNonce === 0) return;
    const everyTrip = new Set<string>();
    vehicleGroups.forEach((group) =>
      group.trips.forEach((points, tripIndex) => {
        // non-trips have nowhere to go, so there's nothing to watch
        if (!isNonTrip(points))
          everyTrip.add(tripKeyFor(group.deviceId, tripIndex));
      })
    );
    if (everyTrip.size === 0) return;
    setSelectedTripKeys(everyTrip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showcaseNonce]);

  // trip keys are indexed within a day, so a day change would otherwise
  // leave the old day's selections and detail state pointing at new trips
  const changeDay = (nextKey: string) => {
    if (!nextKey || nextKey > todayDateKey() || nextKey === dateKey) return;
    setDateKey(nextKey);
    setSelectedTripKeys(new Set());
    setCompareTripKeys([]);
    setIsComparing(false);
    closeTripDetail();
    eventReplay.stopReplay();
  };

  // auto refresh. one request goes out per selected vehicle, so a large
  // selection starts paused rather than hammering the api every 30s - the
  // Resume button turns it back on.
  const AUTO_REFRESH_VEHICLE_LIMIT = 15;
  const autoRefreshTooCostly =
    selectedVehicles.length > AUTO_REFRESH_VEHICLE_LIMIT;
  const [refreshPaused, setRefreshPaused] = useState(false);
  // A day that has already ended reports nothing more, so live updates are
  // off for it rather than paused - there is nothing to resume to. The
  // user's own pause choice is left untouched underneath, so stepping back
  // to today restores whatever they had set.
  const isPastDate = dateKey !== todayDateKey();
  const liveUpdatesHeld = refreshPaused || isPastDate;
  const [refreshFrequency, setRefreshFrequency] = useState(
    APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS[0].value
  );
  // drives the Fleet Map style countdown bar - the ms timestamp of the next
  // poll and the full interval it's counting down from
  const nextRefreshAtRef = useRef<number | null>(null);
  const refreshTotalMsRef = useRef<number>(
    parseInt(refreshFrequency, 10) * 1000
  );
  // read inside the interval without rescheduling it every time they flip
  const isFetchingRef = useRef(isFetching);
  isFetchingRef.current = isFetching;
  const isAnimatingRef = useRef(mapRender.isAnimating);
  isAnimatingRef.current = mapRender.isAnimating;
  // A trip that is open in detail has already finished - there is no more
  // data coming for it, and refetching underneath it only rebuilds its
  // route and pins. The countdown keeps running so the card still reads
  // honestly; it is the fetch that is held.
  const isTripDetailOpenRef = useRef(false);
  isTripDetailOpenRef.current = detailedTripKeys.length > 0;
  // re-pauses whenever the selection grows past the limit, including on a
  // fresh selection made after the user had resumed
  useEffect(() => {
    if (autoRefreshTooCostly) setRefreshPaused(true);
  }, [autoRefreshTooCostly, selectedVehicles]);

  useEffect(() => {
    if (liveUpdatesHeld || selectedVehicles.length === 0) {
      nextRefreshAtRef.current = null;
      return;
    }
    const intervalMs = parseInt(refreshFrequency, 10) * 1000;
    refreshTotalMsRef.current = intervalMs;
    nextRefreshAtRef.current = Date.now() + intervalMs;
    const interval = setInterval(() => {
      // skipping while a round is still in flight stops slow fetches
      // stacking up on each other
      if (
        !isAnimatingRef.current &&
        !isFetchingRef.current &&
        !isTripDetailOpenRef.current
      )
        refetch();
      nextRefreshAtRef.current = Date.now() + intervalMs;
    }, intervalMs);
    return () => clearInterval(interval);
  }, [liveUpdatesHeld, refreshFrequency, selectedVehicles, refetch]);

  // the controls panel and the vehicle legend share the bottom-left corner,
  // so only one of them is ever on screen
  // A trip's detail is open and nothing is playing over it. The panel stays
  // up on the finished route rather than vanishing, so the trip can be
  // replayed from the same place it was watched - and at the speed picked
  // there, since those buttons stay on screen too.
  const detailFinished =
    detailedTripKeys.length > 0 &&
    // the comparison grid covers the map, so nothing belongs on top of it
    !isComparing &&
    !mapRender.isAnimating &&
    !mapRender.isDetailAnimating &&
    !eventReplay.isReplaying &&
    mapRender.detailDrawComplete;

  const showReplayControls =
    mapRender.isAnimating ||
    mapRender.isDetailAnimating ||
    eventReplay.isReplaying ||
    detailFinished;

  // the universal replay clock: whichever animation currently has the map,
  // in the account's timezone. pausing freezes the underlying state, so the
  // displayed time freezes with it - reading the pause is reading the moment.
  const activeReplayClock = eventReplay.isReplaying
    ? eventReplay.replayClock
    : mapRender.isDetailAnimating
      ? mapRender.detailClock
      : mapRender.replayClock;
  const formattedReplayClock =
    activeReplayClock != null
      ? formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(activeReplayClock),
        timezone,
        "h:mm:ss aa"
      )
      : null;

  const availableVehicles = useMemo(
    () => dataOrgVehicles?.results ?? [],
    [dataOrgVehicles]
  );

  const availableGroups = useMemo(
    () => dataOrgGroups?.results ?? [],
    [dataOrgGroups]
  );

  // the Event Filter's options list - whatever status codes actually turn
  // up across every selected vehicle's trips today, same as the asset map
  const filterableStatusCodes = useMemo(() => {
    const codes = new Set<VehicleStatusCode>();
    vehicleGroups.forEach((group) =>
      group.trips.forEach((points) =>
        points.forEach((point) => codes.add(point.statuscode))
      )
    );
    return Array.from(codes);
  }, [vehicleGroups]);

  // "Hide Asset Map Pinpoints" on the account: start the Event Filter with
  // only ignition on/off, so no other event gets a pin. The asset map has
  // always honoured this; this map never read the flag at all, so the
  // setting only ever took effect on one of the two.
  const mapPinsDisabled = `${accountData?.disablemappins}` === "1";
  // seeded once, when the first trips land - after that the Event Filter is
  // the user's to change, and re-seeding on every vehicle change would keep
  // undoing them
  const eventFilterSeededRef = useRef(false);
  useEffect(() => {
    if (eventFilterSeededRef.current || filterableStatusCodes.length === 0)
      return;
    eventFilterSeededRef.current = true;
    if (!mapPinsDisabled) return;
    // the constant rather than an intersection with what's on screen, so a
    // day with no ignition events still seeds the setting rather than an
    // empty selection that pins nothing
    setFilteredStatusCodes(IGNITION_STATUS_CODES);
  }, [filterableStatusCodes, mapPinsDisabled]);

  // an account can rename its status codes - merged across every vehicle on
  // screen rather than picked from one, since there's no single "the
  // vehicle" here the way there is on the asset map
  const statusCodeDescriptions = useMemo(() => {
    const descriptions: Record<number, string> = {};
    availableVehicles.forEach((vehicle) => {
      (vehicle.custom_status_code_names ?? []).forEach((sc) => {
        descriptions[sc.statuscode] =
          sc.description || sc.statusname || "Unknown";
      });
    });
    return descriptions;
  }, [availableVehicles]);

  // whose event is open - resolved from the point itself so it works the
  // same whether it was clicked on the map or in the trip's detail list
  const eventDetailVehicle = useMemo(
    () =>
      selectedTripPoint
        ? availableVehicles.find(
          (vehicle) => vehicle.deviceid === selectedTripPoint.deviceid
        )
        : undefined,
    [selectedTripPoint, availableVehicles]
  );

  return (
    <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
      <div
        // the map needs a real height - h-full collapses to zero here and
        // google renders nothing into a zero-height container
        className={`flex flex-col md:flex-row rounded-lg ${
          userIsImpersonated
            ? "h-[calc(100vh-7rem-40px)]"
            : "h-[calc(100vh-7rem)]"
          } overflow-hidden relative`}
      >
        {/* see the asset map for why these are percentages with min-h-0:
            55vh + 45vh overflows a parent that is only calc(100vh-7rem)
            tall, and a flex item without min-h-0 won't let its
            overflow-auto child scroll */}
        <div className="flex flex-col w-full md:w-72 h-[60%] md:h-full min-h-0 bg-gray-100 order-2 md:order-1">
          {!hasSelection ? (
            <VehicleSelector
              vehicles={availableVehicles}
              groups={availableGroups}
              isLoading={isFetchingOrgVehicles}
              dayStartTimestamp={dayStartTimestamp}
              dayEndTimestamp={dayEndTimestamp}
              initialSelected={selectedVehicles.map((item) => item.deviceId)}
              showDriver={showDriver}
              dateKey={dateKey}
              onChangeDate={changeDay}
              showInactive={showInactiveVehicles}
              onShowInactiveChange={setShowInactiveVehicles}
              onConfirm={(vehicles) => {
                setSelectedVehicles(vehicles);
                setIsChangingSelection(false);
              }}
            />
          ) : detailedTripKeys.length > 0 ? (
            <VehicleTripDetailPanel
              vehicleGroups={vehicleGroups}
              tripKeys={detailedTripKeys}
              activeTripKey={activeTripKey}
              onSetActiveTripKey={setActiveTripKey}
              onHoverTrip={(tripKey) =>
                setHoveredTrip(tripKey ? { tripKey, source: "list" } : null)
              }
              selectedTripPoint={selectedTripPoint}
              setSelectedTripPoint={setSelectedTripPoint}
              onBack={() => {
                eventReplay.stopReplay();
                closeTripDetail();
              }}
              onReplayEvents={(tripKey) => eventReplay.startReplay(tripKey)}
              replayingTripKey={eventReplay.replayingTripKey}
              replayPercentage={eventReplay.replayPercentage}
            />
          ) : (
            <>
              <div className="px-3 py-2 border-b border-gray-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-accent-blue-dark">
                    {selectedVehicles.length} Vehicle
                    {selectedVehicles.length === 1 ? "" : "s"}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      closeTripDetail();
                      setIsComparing(false);
                      setIsChangingSelection(true);
                    }}
                    className="text-xs font-medium text-accent-blue-dark bg-accent-blue-pale rounded-md px-2.5 py-1 hover:bg-accent-blue-paleO66"
                  >
                    Change Selection
                  </button>
                </div>
                <DayStepper dateKey={dateKey} onChange={changeDay} />
              </div>
              {isFetching ? (
                <div className="flex-grow flex items-center justify-center pb-12">
                  <h4 className="font-bold text-lg text-gray-400">
                    Loading vehicle data...
                  </h4>
                </div>
              ) : (
                <VehicleTripList
                  vehicleGroups={vehicleGroups}
                  groups={availableGroups}
                  vehicles={availableVehicles}
                  selectedTripKeys={selectedTripKeys}
                  onToggleTripKey={(key, isChecked) => {
                    setSelectedTripKeys((prev) => {
                      const next = new Set(prev);
                      if (isChecked) next.add(key);
                      else next.delete(key);
                      return next;
                    });
                  }}
                  onSetAllSelected={(isChecked, targetKeys) => {
                    setSelectedTripKeys((prev) => {
                      const next = new Set(prev);
                      targetKeys.forEach((key) =>
                        isChecked ? next.add(key) : next.delete(key)
                      );
                      return next;
                    });
                  }}
                  isReplaying={mapRender.isAnimating}
                  onReplayAll={() => {
                    setIsComparing(false);
                    mapRender.replay();
                  }}
                  hoveredTripKey={hoveredTrip?.tripKey ?? null}
                  onHoverTrip={(tripKey) =>
                    setHoveredTrip(tripKey ? { tripKey, source: "list" } : null)
                  }
                  autoScrollOnHover={hoveredTrip?.source === "map"}
                  detailedTripKeys={detailedTripKeys}
                  onSetDetailedTrip={(tripKey) =>
                    tripKey ? openTripDetail([tripKey]) : closeTripDetail()
                  }
                  onSetDetailedAsset={openAssetDetail}
                  selectedTripPoint={selectedTripPoint}
                  setSelectedTripPoint={setSelectedTripPoint}
                />
              )}
            </>
          )}
        </div>

        <div className="h-[40%] md:h-full md:flex-grow min-h-0 relative order-1 md:order-2">
          {isComparing && (
            <VehicleCompareView
              vehicleGroups={vehicleGroups}
              tripKeys={compareTripKeys}
              timezone={timezone}
              statusCodeDescriptions={statusCodeDescriptions}
              initialFilteredStatusCodes={
                mapPinsDisabled ? IGNITION_STATUS_CODES : filteredStatusCodes
              }
              routeMode={effectiveRouteMode}
              onBack={() => {
                setIsComparing(false);
                requestShowcase();
              }}
              onManageSelection={() => setIsCompareModalOpen(true)}
              setSelectedTripPoint={setSelectedTripPoint}
            />
          )}

          {!!mapState?.mapScriptLoaded ? (
            <BasicMap
              className="bg-gray-200 h-full w-full"
              mapRef={mapRef}
              mapData={mapState?.mapData}
              recordActivityAsPage="heatMap"
              nativeMapControls
              onMapReady={handleMapReady}
            />
          ) : (
            <MapLoadingAnimation />
          )}

          {/* nothing draws until trips are picked, so say so rather than
            leaving an empty map that looks broken */}
          {hasSelection &&
            !isFetching &&
            selectedTripKeys.size === 0 && (
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-[55] flex justify-center pointer-events-none">
                <div className="bg-white/95 rounded-lg shadow-md px-4 py-3 text-sm text-heading-gray888 max-w-[300px] text-center">
                  Pick the trips you want to see from the list on the left, or use
                  Select All.
                </div>
              </div>
            )}

          {/* top-left corner, stacked: the live-updates card leads (same
            shape as the Fleet Map's - collapsed by default, expandable for
            the pause control and the frequency picker), then the replay
            controls when an animation is running, then the route/compare/
            overlap actions in a row underneath. everything that used to be
            scattered around the map's corners lives here now. */}
          <div className="absolute top-5 left-5 z-[60] flex flex-col gap-2 items-start">
            {hasSelection && (
              <div className="relative overflow-hidden w-[220px] bg-white rounded-lg shadow-md px-3 pt-3 pb-2 flex flex-col gap-3 text-xs">
                <div className="flex flex-col gap-3">
                  {liveUpdatesExpanded && (
                    <h4 className="flex items-center justify-between font-semibold text-accent-blue-dark">
                      Live Updates
                      <button
                        type="button"
                        onClick={() => setLiveUpdatesExpanded(false)}
                        aria-label="Collapse live updates"
                        className="size-6 flex items-center justify-center rounded-lg hover:bg-gray-100"
                      >
                        <img
                          src={svgIcons.MinimizeIcon}
                          alt=""
                          className="size-4"
                        />
                      </button>
                    </h4>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 font-semibold text-accent-blue-dark">
                      <img
                        src={
                          liveUpdatesHeld
                            ? svgIcons.UpdatesPaused
                            : svgIcons.UpdatesLive
                        }
                        alt=""
                        className="size-4"
                      />
                      {isPastDate
                        ? "Updates off"
                        : refreshPaused
                          ? "Updates paused"
                          : "Live updates"}
                    </span>
                    {liveUpdatesExpanded ? (
                      // nothing to pause or resume on a day that has ended,
                      // so the control goes rather than sitting there inert
                      isPastDate ? null : (
                        <button
                          type="button"
                          onClick={() => setRefreshPaused((paused) => !paused)}
                          title={
                            refreshPaused && autoRefreshTooCostly
                              ? `Auto refresh is off for selections over ${AUTO_REFRESH_VEHICLE_LIMIT} vehicles`
                              : undefined
                          }
                          className="shrink-0 font-medium text-accent-blue-dark bg-accent-blue-pale rounded-md px-2 py-0.5 hover:bg-accent-blue-paleO66"
                        >
                          {refreshPaused ? "Resume" : "Pause"}
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => setLiveUpdatesExpanded(true)}
                        aria-label="Expand live updates"
                        className="size-6 shrink-0 flex items-center justify-center rounded-lg hover:bg-gray-100"
                      >
                        <img
                          src={svgIcons.ExpandWindowIcon}
                          alt=""
                          className="size-4"
                        />
                      </button>
                    )}
                  </div>
                </div>
                {liveUpdatesExpanded && isPastDate && (
                  <p className="text-heading-gray888">
                    A past date is selected - that day has nothing further to
                    report.
                  </p>
                )}
                <RefreshCountdownBar
                  nextRefreshAtRef={nextRefreshAtRef}
                  refreshTotalMsRef={refreshTotalMsRef}
                  refreshPaused={liveUpdatesHeld}
                  userMapRefreshing={isFetching}
                  refreshFrequency={refreshFrequency}
                />
                {liveUpdatesExpanded && (
                  <>
                    <hr className="border-gray-100" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-heading-gray888">Frequency</span>
                      <select
                        value={refreshFrequency}
                        onChange={(e) => setRefreshFrequency(e.target.value)}
                        className="border border-gray-200 rounded-md px-1.5 py-1 text-xs text-accent-blue-dark"
                      >
                        {APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS.map(
                          (option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowRouteViewsPopup((open) => !open)}
                  title="Route view"
                className={`rounded-lg shadow-md h-10 px-3 flex items-center ${
                  showRouteViewsPopup ? "bg-accent-blue-pale" : "bg-white"
                    } hover:bg-accent-blue-paleO66`}
                >
                  <span className="flex size-5">
                    <img src={svgIcons.LineStyleIcon} alt="Route view" />
                  </span>
                </button>
                {showRouteViewsPopup && (
                  <div className="absolute bg-white shadow-md rounded-lg px-3 pt-3 pb-2 flex flex-col gap-1 top-12 left-0 w-56">
                    {ROUTE_VIEW_OPTIONS.map((option) => (
                      <label
                        key={option.mode}
                        className="flex items-center gap-2 cursor-pointer hover:bg-accent-blue-paleO66 rounded-md py-2 px-2"
                      >
                        <input
                          type="checkbox"
                          className="size-4 accent-accent-blue-dark"
                          checked={routeViews[option.mode]}
                          // the floor rule: default can't come off when it
                          // is the only thing drawing. Disabled rather than
                          // silently snapping back.
                          disabled={routeViewIsLocked(routeViews, option.mode)}
                          onChange={() => applyRouteMode(option.mode)}
                        />
                        <span className="flex-grow text-sm leading-4 text-accent-blue-dark">
                          {option.label}
                        </span>
                        <span className="flex size-6">
                          <img src={option.icon} alt="" className="flex" />
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {hasSelection && (
                <button
                  type="button"
                  onClick={() => setIsCompareModalOpen(true)}
                  className="bg-white rounded-lg shadow-md px-4 h-10 text-sm font-medium text-accent-blue-dark hover:bg-accent-blue-paleO66"
                >
                  Compare Routes
                </button>
              )}

              {/* which event types get a pin - same control and modal as the
                asset map, scoped to whatever's actually on screen here.
                Only where there are event pins to filter: a trip's detail,
                or an event replay. The overview draws routes without pins,
                so the button did nothing there, and the compare view brings
                its own "Show events" control - same as the asset map. */}
              {hasSelection &&
                filterableStatusCodes.length > 0 &&
                (detailedTripKeys.length > 0 || eventReplay.isReplaying) && (
                  <button
                    type="button"
                    onClick={() =>
                      dispatch(
                        setModalsData({ ...modalsState, showEventFilter: true })
                      )
                    }
                    className="bg-white rounded-lg shadow-md px-4 h-10 text-sm font-medium text-accent-blue-dark hover:bg-accent-blue-paleO66"
                  >
                    Event Filter
                  </button>
                )}

              {/* sits with the other map actions rather than off in a corner -
                showing where routes pile up is an action like the rest */}
              {hasSelection && vehicleGroups.length > 0 && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={overlapsEnabled}
                  onClick={() => setOverlapsEnabled((enabled) => !enabled)}
                  title={
                    overlapsEnabled
                      ? "Hide where routes overlap"
                      : "Show where routes overlap"
                  }
                className={`rounded-lg shadow-md px-4 h-10 flex items-center gap-2.5 text-sm font-medium hover:bg-accent-blue-paleO66 ${
                  overlapsEnabled
                      ? "bg-accent-blue-pale text-accent-blue-dark"
                      : "bg-white text-heading-gray888"
                    }`}
                >
                  <span>Overlaps</span>
                  <span
                  className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                    overlapsEnabled ? "bg-accent-blue-dark" : "bg-gray-300"
                      }`}
                  >
                    <span
                    className={`inline-block size-3 rounded-full bg-white transition-transform ${
                      overlapsEnabled ? "translate-x-3.5" : "translate-x-0.5"
                        }`}
                    />
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* replay/draw controls and the universal clock - top-centre, same
            as the compare view's expanded map, rather than crowding into
            either side corner */}
          {showReplayControls && (
            <div className="absolute top-5 left-1/2 -translate-x-1/2 z-[60]">
              <ReplayControlsPanel
                label={
                  eventReplay.isReplaying
                    ? "Replaying events"
                    : mapRender.isDetailAnimating
                      ? "Drawing route"
                      : mapRender.isReplayingAll
                        ? "Replaying routes"
                        : detailFinished
                          ? detailedTripKeys.length > 1
                            ? "Trip routes"
                            : "Trip route"
                          : "Drawing routes"
                }
                isFinished={detailFinished}
                onReplay={mapRender.replayDetail}
                isPaused={
                  eventReplay.isReplaying
                    ? eventReplay.isPaused
                    : mapRender.isPaused
                }
                onTogglePause={() =>
                  eventReplay.isReplaying
                    ? eventReplay.togglePause()
                    : mapRender.togglePause()
                }
                onStop={() =>
                  eventReplay.isReplaying
                    ? eventReplay.stopReplay()
                    : mapRender.isDetailAnimating
                      ? mapRender.stopDetailAnimation()
                      : mapRender.stopReplay()
                }
                progressPercent={
                  eventReplay.isReplaying
                    ? eventReplay.replayPercentage
                    : mapRender.isDetailAnimating
                      ? mapRender.detailProgress
                      : mapRender.progress
                }
                formattedClock={formattedReplayClock}
                vehicleSpeedLabel={formatSpeed(
                  eventReplay.replaySpeedKph ?? mapRender.detailSpeedKph
                )}
                currentSpeed={
                  eventReplay.isReplaying
                    ? eventReplay.replaySpeed
                    : mapRender.replaySpeed
                }
                onSetSpeed={(speed) =>
                  eventReplay.isReplaying
                    ? eventReplay.setReplaySpeed(speed)
                    : mapRender.setReplaySpeed(speed)
                }
              />
            </div>
          )}

          {/* the asset map's event popup, reused as-is. the event carries its
            own deviceid, so the right vehicle record is a lookup rather
            than something that has to be threaded through the click. */}
          {!!eventDetailVehicle && (
            <ModalVehicleTripEventDetails
              vehicleId={eventDetailVehicle.deviceid}
              vehicleData={eventDetailVehicle}
              tripPointData={selectedTripPoint}
              setSelectedTripPoint={setSelectedTripPoint}
              onEventDetailModalClose={() => {
                setSelectedTripPoint(null);
                mapRender.highlightEventPoint(null);
              }}
            />
          )}

          {/* same modal the asset map uses, self-gated on the redux
            showEventFilter flag - the Event Filter button above just sets
            it. Held back while comparing: the compare view mounts its own
            copy for the trips on its cards, and both read that one flag, so
            leaving this up would open two stacked filters. */}
          {!isComparing && (
            <ModalEventFilter
              statusCodes={filterableStatusCodes}
              filteredStatusCodes={filteredStatusCodes}
              handleEventFiltersChange={setFilteredStatusCodes}
              statusCodeDescriptions={statusCodeDescriptions}
            />
          )}

          <CompareVehicleRoutesModal
            visible={isCompareModalOpen}
            vehicleGroups={vehicleGroups}
            selectedKeys={compareTripKeys}
            maxVehicles={MAX_COMPARE_VEHICLES}
            onHide={() => setIsCompareModalOpen(false)}
            onConfirm={(keys) => {
              setCompareTripKeys(keys);
              setIsComparing(true);
              setIsCompareModalOpen(false);
              closeTripDetail();
            }}
          />
        </div>
      </div>
    </div>
  );
};

// the picker lists, groups and counts client-side, so one page has to hold
// everything it will show. 5000 is the most the listing endpoint will
// return, which is as far as asking for everything can go.
const VEHICLE_LIST_PAGE_SIZE = 5000;

export default ScreenMapMultiVehicle;
